/**
 * @class
 * @classdesc HcS configuration history
 *
 * Stores (sub-) configuration call history.
 * 
 * Usage:
 * =======
 * Whenever a user enters a subconfiguration use the {@link HcsSubconfigHistory#add|add()} function to place the new configuration
 * upon history stack. Each item contains the name/ident (which will be used to force ECS to enter this subconfiguration),
 * the configuration type/mode as defined in the ConfigurationModes enum.
 * Optionally the current selectd group in configuration can be set.
 * 
 * The {@link HcsSubconfigHistory#last|last()} property returns the last/current item on history stack.
 * 
 * When exiting the current subconfiguration call the {@link HcsSubconfigHistory#pop|pop()} function to remove this item from stack.
 * After that the "last" property points to the previous configuration.
 * 
 * Every time the user changes the group of the given configuration ensure to call the {@link HcsSubconfigHistory#changeGroup|changeGroup()} function.
 * This can be handy to jump back to the last selected group in the previous configuration when exiting the current one.
 */
export default class HcsSubconfigHistory
{
    constructor() {
        this.history = [];
    }

    /**
     * Add new configuration object.
     * @param {string} method - ECS merthod name
     * @param {string} subConfigName - Ident of subconfiguration
     * @param {ConfigurationModes} subConfigMode - Configuration mode.
     * @param {string} [selectedGroup] - Currently selected group for the new configuration to be added.
     */
    add(method, subConfigName, subConfigMode, selectedGroup = null) {
        this.history.push({
            method: method,
            subConfigName: subConfigName, 
            subConfigMode: subConfigMode,
            selectedGroup: selectedGroup
        });
    }

    /**
     * Remove last added configuration item.
     * @returns {object} Last configuration object which has been removed from history.
     */
    pop() {
        return this.history.pop();
    }

    /**
     * Clear entire history.
     */
    clear() {
        this.history = [];
    }

    /**
     * Set active group for current configuration.
     * @param {string} groupIdent - Group ident.
     */
    changeGroup(groupIdent) {
        if(!this.last) {
            return false;
        }

        this.last.selectedGroup = groupIdent;

        return true;
    }

    /**
     * Return debug information object.
     * For better reading we convert the "subConfigMode"
     * enum value into a readable text item.
     */
    get debug() {
        return this.history.map(item => {
            let newObj = Object.assign({}, item);

            newObj.subConfigMode = item.subConfigMode.domAttr;
            return newObj;
        });
    }

    /**
     * Get current history item.
     * @returns {object} Current history item.
     */
    get last() {
        return this.history.length ? this.history[this.history.length - 1] : null;
    }
}